import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  published_at: string;
  featured_image: string | null;
}

interface RelatedArticlesSectionProps {
  currentSlug: string;
  categorySlug?: string;
}

const RelatedArticlesSection = ({ currentSlug, categorySlug }: RelatedArticlesSectionProps) => {
  const [articles, setArticles] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelatedArticles();
  }, [currentSlug, categorySlug]);

  const fetchRelatedArticles = async () => {
    let query = supabase
      .from("blog_posts")
      .select("id, title, slug, excerpt, published_at, featured_image")
      .eq("published", true)
      .neq("slug", currentSlug)
      .order("published_at", { ascending: false })
      .limit(3);

    if (categorySlug) {
      // Filter by category if provided
      const { data: categoryData } = await supabase
        .from("blog_categories")
        .select("id")
        .eq("slug", categorySlug)
        .single();

      if (categoryData) {
        query = query.eq("category_id", categoryData.id);
      }
    }

    const { data, error } = await query;

    if (!error && data) {
      setArticles(data);
    }
    setLoading(false);
  };

  if (loading || articles.length === 0) {
    return null;
  }

  return (
    <section className="my-16 py-12 border-t border-b">
      <h2 className="text-3xl font-bold text-center mb-4">Related Articles</h2>
      <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
        Continue exploring insights on commerce operations and retail technology
      </p>
      <div className="grid md:grid-cols-3 gap-6">
        {articles.map((article) => (
          <Card key={article.id} className="overflow-hidden hover:shadow-lg transition-all hover:border-primary/50 group">
            {article.featured_image && (
              <div className="h-48 overflow-hidden">
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <Calendar className="h-4 w-4" />
                {new Date(article.published_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
              <h3 className="text-lg font-bold mb-2 line-clamp-2">{article.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{article.excerpt}</p>
              <Button variant="link" className="p-0 h-auto" asChild>
                <Link to={`/blog/${article.slug}`}>
                  Read article <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticlesSection;
